import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { getUserProfile, changePassword } from "../api/services/profileService";
import Layout from "../layouts/Layout";
import AdminLayout from "../layouts/AdminLayout";
import CompanyUserLayout from "../layouts/CompanyUserLayout";
import UniversityStudentLayout from "../layouts/UniversityStudentLayout";
import TwoFactorSetup from "./TwoFactorSetup";
import {
  Mail,
  User,
  Shield,
  Award,
  Clock,
  Activity,
  TrendingUp,
  Edit3,
  Settings,
  Key,
  Bell,
  Globe,
  Lock,
  CheckCircle,
  AlertCircle,
  X,
  Eye,
  EyeOff,
  Zap,
  BarChart2,
  ChevronRight,
} from "lucide-react";

export default function Profile() {
  const { user } = useAuth();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [showTwoFAModal, setShowTwoFAModal] = useState(false);

  const isPlatformAdmin = user?.roles?.includes("platform_admin");
  const isCompanyUser = user?.roles?.includes("company_user");
  const isUniversityStudent = user?.roles?.includes("university_student");

  const LayoutWrapper = isPlatformAdmin
    ? AdminLayout
    : isCompanyUser
      ? CompanyUserLayout
      : isUniversityStudent
        ? UniversityStudentLayout
        : Layout;

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const data = await getUserProfile();
        setProfileData(data);
        setError(null);
      } catch (err) {
        setError(err.message || "Failed to load profile");
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError("New passwords do not match");
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      setPasswordError("Password must be at least 8 characters");
      return;
    }

    setIsChangingPassword(true);
    try {
      await changePassword(
        passwordForm.currentPassword,
        passwordForm.newPassword,
        passwordForm.confirmPassword,
      );
      setPasswordSuccess("Password updated successfully!");
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setTimeout(() => {
        setShowPasswordModal(false);
        setPasswordSuccess("");
      }, 2000);
    } catch (err) {
      setPasswordError(err.message || "Failed to change password");
    } finally {
      setIsChangingPassword(false);
    }
  };

  const displayName =
    profileData?.given_name && profileData?.family_name
      ? `${profileData.given_name} ${profileData.family_name}`
      : profileData?.preferred_username || user?.username || "User";

  const avatarLetter =
    profileData?.preferred_username?.[0]?.toUpperCase() ||
    user?.username?.[0]?.toUpperCase() ||
    "U";

  const userRole = profileData?.roles?.[0] || user?.roles?.[0] || "User";

  if (loading) {
    return (
      <LayoutWrapper>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="flex flex-col items-center gap-3">
            <div className="relative w-10 h-10">
              <div className="absolute inset-0 border-2 border-(--color-primary)/20 rounded-full" />
              <div className="absolute inset-0 border-2 border-transparent border-t-(--color-primary) rounded-full animate-spin" />
            </div>
            <p className="text-(--color-text-muted) text-sm tracking-wide">
              Loading profile…
            </p>
          </div>
        </div>
      </LayoutWrapper>
    );
  }

  if (error) {
    return (
      <LayoutWrapper>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-red-400 text-sm">{error}</div>
        </div>
      </LayoutWrapper>
    );
  }

  return (
    <LayoutWrapper>
      <div className="min-h-screen w-full max-w-350 mx-auto px-6 py-8 space-y-8">
        {/* ── Page Header ── */}
        <div className="flex items-end justify-between">
          <div>
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-(--color-primary) mb-1">
              Account
            </p>
            <h1 className="text-3xl font-bold text-(--color-text-primary) mt-1 leading-tight [text-shadow:0_0_12px_var(--color-shadow-primary)]">
              User Profile
            </h1>
          </div>
          <button className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-(--color-primary) text-(--color-text-on-primary) hover:opacity-90 transition-opacity">
            <Edit3 className="h-3.5 w-3.5" />
            Edit Profile
          </button>
        </div>

        {/* ── Main Grid ── */}
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
          {/* ── Left Column ── */}
          <div className="space-y-4">
            {/* Identity Card */}
            <div className="bg-(--color-bg-card) border border-(--color-border) rounded-2xl p-6 flex flex-col items-center text-center">
              {/* Avatar */}
              <div className="relative mb-4">
                <div className="w-20 h-20 rounded-2xl bg-(--color-primary) flex items-center justify-center text-3xl font-bold text-(--color-text-on-primary)">
                  {avatarLetter}
                </div>
                <span className="absolute -bottom-1.5 -right-1.5 w-4 h-4 rounded-full bg-green-400 border-2 border-(--color-bg-card)" />
              </div>

              <h2 className="text-lg font-bold text-(--color-text-primary) leading-tight">
                {displayName}
              </h2>
              <span className="mt-1 inline-block text-xs font-medium px-2.5 py-0.5 rounded-full bg-(--color-primary)/10 text-(--color-primary) border border-(--color-primary)/20">
                {userRole}
              </span>

              {/* Divider */}
              <div className="w-full h-px bg-(--color-border) my-5" />

              {/* Stats row */}
              <div className="w-full grid grid-cols-3 gap-2">
                {[
                  { value: "24", label: "Labs" },
                  { value: "156h", label: "Training" },
                  { value: "12", label: "Certs" },
                ].map(({ value, label }) => (
                  <div
                    key={label}
                    className="flex flex-col items-center gap-0.5"
                  >
                    <span className="text-lg font-bold text-(--color-primary)">
                      {value}
                    </span>
                    <span className="text-[11px] text-(--color-text-muted)">
                      {label}
                    </span>
                  </div>
                ))}
              </div>

              {/* Divider */}
              <div className="w-full h-px bg-(--color-border) my-5" />

              {/* Quick actions */}
              <div className="w-full space-y-2">
                <button
                  onClick={() => setShowPasswordModal(true)}
                  className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm text-(--color-text-primary) bg-(--color-bg) hover:bg-(--color-primary)/10 border border-(--color-border) hover:border-(--color-primary)/40 transition-all duration-200 group"
                >
                  <Settings className="h-4 w-4 text-(--color-text-muted) group-hover:text-(--color-primary) transition-colors" />
                  <span>Change Password</span>
                  <ChevronRight className="h-3.5 w-3.5 ml-auto text-(--color-text-muted) group-hover:text-(--color-primary) transition-colors" />
                </button>
                <button
                  onClick={() => setShowTwoFAModal(true)}
                  className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm text-(--color-text-primary) bg-(--color-bg) hover:bg-(--color-primary)/10 border border-(--color-border) hover:border-(--color-primary)/40 transition-all duration-200 group"
                >
                  <Key className="h-4 w-4 text-(--color-text-muted) group-hover:text-(--color-primary) transition-colors" />
                  <span>Configure 2FA</span>
                  <ChevronRight className="h-3.5 w-3.5 ml-auto text-(--color-text-muted) group-hover:text-(--color-primary) transition-colors" />
                </button>
              </div>
            </div>

            {/* Activity Status Card */}
            <div className="bg-(--color-bg-card) border border-(--color-border) rounded-2xl p-5">
              <p className="text-xs font-semibold tracking-[0.15em] uppercase text-(--color-text-muted) mb-4">
                Status
              </p>
              <div className="space-y-3.5">
                {[
                  {
                    label: "Account",
                    value: "Active",
                    valueClass: "text-green-400 flex items-center gap-1.5",
                    dot: true,
                  },
                  { label: "Last Login", value: "2 hours ago" },
                  { label: "Member Since", value: "Jan 2026" },
                ].map(({ label, value, valueClass, dot }) => (
                  <div
                    key={label}
                    className="flex items-center justify-between"
                  >
                    <span className="text-xs text-(--color-text-muted)">
                      {label}
                    </span>
                    <span
                      className={`text-xs font-medium ${valueClass || "text-(--color-text-primary)"}`}
                    >
                      {dot && (
                        <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse inline-block" />
                      )}
                      {value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── Right Column ── */}
          <div className="space-y-4">
            {/* Tab Bar */}
            <div className="bg-(--color-bg-card) border border-(--color-border) rounded-2xl overflow-hidden">
              <div className="flex border-b border-(--color-border)">
                {["overview", "details", "security", "activity"].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`flex-1 py-3.5 text-xs font-semibold tracking-wide uppercase transition-all duration-200 relative ${
                      activeTab === tab
                        ? "text-(--color-primary)"
                        : "text-(--color-text-muted) hover:text-(--color-text-primary)"
                    }`}
                  >
                    {tab}
                    {activeTab === tab && (
                      <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-(--color-primary)" />
                    )}
                  </button>
                ))}
              </div>

              <div className="p-6">
                {/* ── Overview Tab ── */}
                {activeTab === "overview" && (
                  <div className="space-y-8">
                    {/* Performance Metrics */}
                    <div>
                      <p className="text-xs font-semibold tracking-[0.15em] uppercase text-(--color-text-muted) mb-4 flex items-center gap-2">
                        <BarChart2 className="h-3.5 w-3.5" />
                        Performance
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {[
                          {
                            value: "850",
                            label: "Security Score",
                            pct: 85,
                            color: "bg-(--color-primary)",
                            icon: Award,
                            iconColor: "text-(--color-primary)",
                          },
                          {
                            value: "48.5h",
                            label: "Training Hours",
                            pct: 65,
                            color: "bg-blue-400",
                            icon: Clock,
                            iconColor: "text-blue-400",
                          },
                          {
                            value: "12",
                            label: "Completed Labs",
                            pct: 40,
                            color: "bg-amber-400",
                            icon: CheckCircle,
                            iconColor: "text-amber-400",
                          },
                        ].map(
                          ({
                            value,
                            label,
                            pct,
                            color,
                            icon: Icon,
                            iconColor,
                          }) => (
                            <div
                              key={label}
                              className="bg-(--color-bg) border border-(--color-border) rounded-xl p-4 space-y-3"
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-xl font-bold text-(--color-text-primary)">
                                    {value}
                                  </p>
                                  <p className="text-[11px] text-(--color-text-muted) mt-0.5">
                                    {label}
                                  </p>
                                </div>
                                <Icon
                                  className={`h-5 w-5 ${iconColor} opacity-80`}
                                />
                              </div>
                              <div className="h-1 rounded-full bg-(--color-border) overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${color} transition-all duration-700`}
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </div>
                          ),
                        )}
                      </div>
                    </div>

                    {/* Achievements */}
                    <div>
                      <p className="text-xs font-semibold tracking-[0.15em] uppercase text-(--color-text-muted) mb-4 flex items-center gap-2">
                        <Award className="h-3.5 w-3.5" />
                        Recent Achievements
                      </p>
                      <div className="space-y-2">
                        {[
                          {
                            name: "Network Security Expert",
                            date: "2 days ago",
                            icon: Shield,
                          },
                          {
                            name: "100 Hours Training",
                            date: "1 week ago",
                            icon: Clock,
                          },
                          {
                            name: "Perfect Score",
                            date: "2 weeks ago",
                            icon: Zap,
                          },
                        ].map(({ name, date, icon: Icon }, idx) => (
                          <div
                            key={idx}
                            className="flex items-center gap-4 px-4 py-3 rounded-xl border border-(--color-border) hover:border-(--color-primary)/40 hover:bg-(--color-primary)/5 transition-all duration-200"
                          >
                            <div className="w-8 h-8 rounded-lg bg-(--color-primary)/10 flex items-center justify-center shrink-0">
                              <Icon className="h-4 w-4 text-(--color-primary)" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-(--color-text-primary) truncate">
                                {name}
                              </p>
                              <p className="text-[11px] text-(--color-text-muted)">
                                {date}
                              </p>
                            </div>
                            <CheckCircle className="h-4 w-4 text-green-400 shrink-0" />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Details Tab ── */}
                {activeTab === "details" && (
                  <div className="space-y-6">
                    <p className="text-xs font-semibold tracking-[0.15em] uppercase text-(--color-text-muted) flex items-center gap-2">
                      <User className="h-3.5 w-3.5" />
                      Personal Information
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {[
                        {
                          icon: User,
                          label: "Username",
                          value:
                            profileData?.preferred_username ||
                            user?.username ||
                            "N/A",
                        },
                        {
                          icon: Shield,
                          label: "Role",
                          value:
                            profileData?.roles?.[0] ||
                            user?.roles?.[0] ||
                            "N/A",
                        },
                        {
                          icon: User,
                          label: "First Name",
                          value: profileData?.given_name || "N/A",
                        },
                        {
                          icon: User,
                          label: "Last Name",
                          value: profileData?.family_name || "N/A",
                        },
                      ].map(({ icon: Icon, label, value }) => (
                        <div
                          key={label}
                          className="bg-(--color-bg) border border-(--color-border) rounded-xl px-4 py-3.5"
                        >
                          <div className="flex items-center gap-2 mb-1.5">
                            <Icon className="h-3.5 w-3.5 text-(--color-primary)" />
                            <span className="text-[11px] text-(--color-text-muted) uppercase tracking-wider font-medium">
                              {label}
                            </span>
                          </div>
                          <p className="text-sm font-semibold text-(--color-text-primary) pl-5">
                            {value}
                          </p>
                        </div>
                      ))}
                    </div>

                    {/* Email — full width */}
                    <div className="bg-(--color-bg) border border-(--color-border) rounded-xl px-4 py-3.5">
                      <div className="flex items-center gap-2 mb-1.5">
                        <Mail className="h-3.5 w-3.5 text-(--color-primary)" />
                        <span className="text-[11px] text-(--color-text-muted) uppercase tracking-wider font-medium">
                          Email
                        </span>
                      </div>
                      <p className="text-sm font-semibold text-(--color-text-primary) pl-5">
                        {profileData?.email || user?.email || "N/A"}
                      </p>
                    </div>
                  </div>
                )}

                {/* ── Security Tab ── */}
                {activeTab === "security" && (
                  <div className="space-y-6">
                    <p className="text-xs font-semibold tracking-[0.15em] uppercase text-(--color-text-muted) flex items-center gap-2">
                      <Lock className="h-3.5 w-3.5" />
                      Security Settings
                    </p>
                    <div className="space-y-2">
                      {[
                        {
                          label: "Two-Factor Authentication",
                          status: "Enabled",
                          icon: CheckCircle,
                          statusClass: "text-green-400 bg-green-400/10",
                        },
                        {
                          label: "Password Last Changed",
                          status: "30 days ago",
                          icon: Key,
                          statusClass:
                            "text-(--color-text-muted) bg-(--color-bg)",
                        },
                        {
                          label: "Active Sessions",
                          status: "2 devices",
                          icon: Globe,
                          statusClass: "text-blue-400 bg-blue-400/10",
                        },
                        {
                          label: "Login Notifications",
                          status: "Enabled",
                          icon: Bell,
                          statusClass: "text-green-400 bg-green-400/10",
                        },
                      ].map(({ label, status, icon: Icon, statusClass }) => (
                        <div
                          key={label}
                          className="flex items-center justify-between px-4 py-3.5 bg-(--color-bg) border border-(--color-border) rounded-xl hover:border-(--color-primary)/30 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-(--color-primary)/10 flex items-center justify-center">
                              <Icon className="h-4 w-4 text-(--color-primary)" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-(--color-text-primary)">
                                {label}
                              </p>
                              <span
                                className={`inline-block mt-0.5 text-[11px] font-medium px-2 py-0.5 rounded-full ${statusClass}`}
                              >
                                {status}
                              </span>
                            </div>
                          </div>
                          <button className="text-xs font-semibold text-(--color-primary) hover:underline underline-offset-2 transition-opacity hover:opacity-70">
                            Configure
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── Activity Tab ── */}
                {activeTab === "activity" && (
                  <div className="space-y-6">
                    <p className="text-xs font-semibold tracking-[0.15em] uppercase text-(--color-text-muted) flex items-center gap-2">
                      <Activity className="h-3.5 w-3.5" />
                      Recent Activity
                    </p>
                    <div className="relative pl-6 space-y-0">
                      {/* Timeline line */}
                      <div className="absolute left-2.75 top-2 bottom-2 w-px bg-(--color-border)" />

                      {[
                        {
                          action: "Completed Lab: Web Application Security",
                          time: "2 hours ago",
                          type: "success",
                        },
                        {
                          action: "Started Course: Advanced Network Security",
                          time: "1 day ago",
                          type: "info",
                        },
                        {
                          action: "Earned Achievement: Security Expert",
                          time: "2 days ago",
                          type: "achievement",
                        },
                        {
                          action: "Updated Profile Settings",
                          time: "3 days ago",
                          type: "update",
                        },
                        {
                          action: "Logged in from new device",
                          time: "5 days ago",
                          type: "security",
                        },
                      ].map(({ action, time, type }, idx) => {
                        const dotColor = {
                          success: "bg-green-400",
                          info: "bg-blue-400",
                          achievement: "bg-amber-400",
                          update: "bg-(--color-primary)",
                          security: "bg-red-400",
                        }[type];

                        return (
                          <div
                            key={idx}
                            className="relative flex gap-4 pb-5 last:pb-0"
                          >
                            {/* Dot */}
                            <div
                              className={`absolute -left-4.25 mt-1.5 w-2.5 h-2.5 rounded-full border-2 border-(--color-bg-card) ${dotColor} shrink-0`}
                            />
                            <div className="flex-1 bg-(--color-bg) border border-(--color-border) rounded-xl px-4 py-3 hover:border-(--color-primary)/30 transition-colors">
                              <p className="text-sm text-(--color-text-primary) font-medium">
                                {action}
                              </p>
                              <p className="text-[11px] text-(--color-text-muted) mt-0.5">
                                {time}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Change Password Modal ── */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-(--color-bg-card) border border-(--color-border) rounded-2xl w-full max-w-md p-6 shadow-2xl">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-lg font-bold text-(--color-text-primary)">
                  Change Password
                </h2>
                <p className="text-xs text-(--color-text-muted) mt-0.5">
                  Use a strong password you haven't used elsewhere.
                </p>
              </div>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordError("");
                  setPasswordSuccess("");
                  setPasswordForm({
                    currentPassword: "",
                    newPassword: "",
                    confirmPassword: "",
                  });
                }}
                className="text-(--color-text-muted) hover:text-(--color-text-primary) transition-colors p-1"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {passwordError && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-xs flex items-center gap-2">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {passwordError}
              </div>
            )}
            {passwordSuccess && (
              <div className="mb-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400 text-xs flex items-center gap-2">
                <CheckCircle className="h-4 w-4 shrink-0" />
                {passwordSuccess}
              </div>
            )}

            <form onSubmit={handlePasswordChange} className="space-y-4">
              {[
                {
                  key: "current",
                  label: "Current Password",
                  field: "currentPassword",
                },
                { key: "new", label: "New Password", field: "newPassword" },
                {
                  key: "confirm",
                  label: "Confirm New Password",
                  field: "confirmPassword",
                },
              ].map(({ key, label, field }) => (
                <div key={key} className="space-y-1.5">
                  <label className="block text-xs font-medium text-(--color-text-muted) uppercase tracking-wider">
                    {label}
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords[key] ? "text" : "password"}
                      required
                      value={passwordForm[field]}
                      onChange={(e) =>
                        setPasswordForm({
                          ...passwordForm,
                          [field]: e.target.value,
                        })
                      }
                      className="w-full px-3.5 py-2.5 pr-10 bg-(--color-bg) border border-(--color-border) rounded-lg text-sm text-(--color-text-primary) placeholder:text-(--color-text-muted)/40 focus:outline-none focus:border-(--color-primary) transition-colors"
                      placeholder={`Enter ${label.toLowerCase()}`}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowPasswords({
                          ...showPasswords,
                          [key]: !showPasswords[key],
                        })
                      }
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-(--color-text-muted) hover:text-(--color-primary) transition-colors"
                    >
                      {showPasswords[key] ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
              ))}

              <p className="text-[11px] text-(--color-text-muted)">
                Use 8+ characters with uppercase, lowercase, and numbers.
              </p>

              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordError("");
                    setPasswordSuccess("");
                    setPasswordForm({
                      currentPassword: "",
                      newPassword: "",
                      confirmPassword: "",
                    });
                  }}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold rounded-lg border border-(--color-border) text-(--color-text-muted) hover:text-(--color-text-primary) hover:border-(--color-primary)/40 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isChangingPassword}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold rounded-lg bg-(--color-primary) text-(--color-text-on-primary) hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isChangingPassword ? "Updating…" : "Update Password"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── 2FA Modal ── */}
      {showTwoFAModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="relative max-w-5xl w-full max-h-[85vh]">
            <button
              onClick={() => setShowTwoFAModal(false)}
              className="absolute top-4 right-4 z-20 w-8 h-8 flex items-center justify-center rounded-lg bg-(--color-bg-card) border border-(--color-border) text-(--color-text-muted) hover:text-(--color-primary) transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="overflow-y-auto max-h-[85vh] rounded-2xl">
              <TwoFactorSetup embedded />
            </div>
          </div>
        </div>
      )}
    </LayoutWrapper>
  );
}
